import {
  message as AntMessage,
  Modal as AntModal,
  notification as AntNotification,
} from 'ant-design-vue'

import {
  InfoCircleFilled,
  CloseCircleFilled,
  CheckCircleFilled,
  ExclamationCircleFilled,
} from '@ant-design/icons-vue'

import type { ModalFuncProps } from 'ant-design-vue/lib/modal/Modal'
import type { IconType } from 'ant-design-vue/lib/notification'

export type ModalFuncPropsEx = Omit<ModalFuncProps, 'iconType'> & {
  iconType: IconType
}

export type ModalFuncPropsPartial = Partial<ModalFuncPropsEx> & Pick<ModalFuncPropsEx, 'content'>

export const getIcon = (iconType: IconType) => {
  if (iconType === 'success') {
    return <CheckCircleFilled class="modal-icon-success" />
  } else if (iconType === 'info') {
    return <InfoCircleFilled class="modal-icon-info" />
  } else if (iconType === 'warning') {
    return <ExclamationCircleFilled class="modal-icon-warning" />
  } else {
    return <CloseCircleFilled class="modal-icon-error" />
  }
}

export const getModalContent = (content: ModalFuncPropsEx['content']) => {
  if (typeof content === 'string') {
    return <div innerHTML={`<div>${content}</div>`}></div>
  }
  return content
}

export const createConfirm = (opts: ModalFuncPropsEx) => {
  const iconType = opts.iconType
  Reflect.deleteProperty(opts, 'iconType')
  return AntModal.confirm({
    centered: true,
    icon: getIcon(iconType),
    ...opts,
  })
}

export const createModalOptions = (
  opts: ModalFuncPropsPartial,
  iconType: IconType,
): ModalFuncPropsPartial => {
  return {
    okText: '确 定',
    centered: true,
    ...opts,
    icon: getIcon(iconType),
    content: getModalContent(opts.content),
  }
}

const createSuccessModal = (opts: ModalFuncPropsPartial) => {
  return AntModal.success(createModalOptions(opts, 'success'))
}

const createInfoModal = (opts: ModalFuncPropsPartial) => {
  return AntModal.info(createModalOptions(opts, 'info'))
}

const createErrorModal = (opts: ModalFuncPropsPartial) => {
  return AntModal.error(createModalOptions(opts, 'error'))
}

const createWarningModal = (opts: ModalFuncPropsPartial) => {
  return AntModal.warning(createModalOptions(opts, 'warning'))
}

AntMessage.config({
  top: '30px',
  duration: 2,
  maxCount: 1,
})

AntNotification.config({
  placement: 'topRight',
  top: '30px',
  duration: 3,
})

export function useMessage() {
  return {
    createMessage: AntMessage,
    notification: AntNotification,
    createConfirm,
    createSuccessModal,
    createInfoModal,
    createErrorModal,
    createWarningModal,
    destroyAllModal: AntModal.destroyAll,
  }
}
